import React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { auth } from '../lib/firebase';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { trackClarityEvent } from '../lib/clarity';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();

  // If user is already logged in, redirect to home
  if (user) return <Navigate to="/" />;
  
  // Don't show anything while checking auth state
  if (isLoading) return null;

  const handleGoogleSignIn = async () => {
    try {
      trackClarityEvent('login_attempt', { method: 'google' });
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // If new user, create document with 5 free credits
        await setDoc(doc(db, 'users', user.uid), {
          credits: 5,
          email: user.email,
          name: user.displayName,
          createdAt: new Date(),
        });
        trackClarityEvent('user_signup', { method: 'google' });
      } else {
        trackClarityEvent('login_success', { method: 'google' });
      }

      navigate('/');
    } catch (error) {
      console.error('Error signing in with Google:', error);
      trackClarityEvent('login_error', { 
        method: 'google', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1012] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#1d3d39] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
            <img
              src="/logo.png"
              alt="Yesil AI"
              className="w-12 h-12 object-contain"
            />
          </div>
          <h1 className="text-2xl font-semibold text-gray-100 mb-2">Welcome to Yesil Health AI</h1>
          <p className="text-gray-400">
            Access specialized medical AI consultations powered by evidence-based resources
          </p>
        </div>

        <div className="bg-[#1A1B1E] rounded-2xl border border-gray-800 shadow-sm p-6">
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-[#00B5A1]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-gray-400">Get 5 free credits upon signup</span>
            </div>
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-[#00B5A1]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-gray-400">Evidence-based responses</span>
            </div>
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-[#00B5A1]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-gray-400">24/7 availability</span>
            </div>
          </div>

          <button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#232429] text-gray-300 font-medium rounded-xl border border-gray-700 hover:bg-[#292a32] transition-colors"
          >
            <img src="/google.svg" alt="Google" className="w-5 h-5" />
            <span>Continue with Google</span>
          </button>

          <p className="mt-4 text-xs text-center text-gray-500">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}; 